import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    Alert,
    Button,
    CircularProgress,
    Grid,
    Avatar,
    LinearProgress,
    alpha,
    useTheme,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Assignment as AssignmentIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    Schedule as ScheduleIcon,
    VideoLibrary as VideoIcon,
    EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import StudentLayout from 'components/layout/StudentLayout';
import { getLiveProblemsBySession, getUserSubmissions, ProblemSubmission, Problem } from 'services/problemService';
import { getSessionById, getLiveProblemGroupsBySession, Session, ProblemGroup } from 'services/sessionService';
import { createTextPreview } from 'utils/textUtils';

const ProblemsPageV2 = () => {
    const theme = useTheme();
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [problems, setProblems] = useState<Problem[]>([]);
    const [submissions, setSubmissions] = useState<ProblemSubmission[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [groups, setGroups] = useState<{ [key: number]: Problem[] }>({});
    const [session, setSession] = useState<Session | null>(null);
    const [problemGroups, setProblemGroups] = useState<ProblemGroup[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!sessionId) {
                    setError('Invalid session ID');
                    setLoading(false);
                    return;
                }

                const sessionIdNum = parseInt(sessionId);

                const [
                    fetchedSession,
                    fetchedProblemGroups,
                    fetchedProblems,
                    userSubmissions
                ] = await Promise.all([
                    getSessionById(sessionIdNum),
                    getLiveProblemGroupsBySession(sessionIdNum),
                    getLiveProblemsBySession(sessionIdNum),
                    getUserSubmissions()
                ]);

                const processedGroups = fetchedProblemGroups.map(group => ({
                    ...group,
                    id: Number(group.id),
                    sessionId: Number(group.sessionId)
                }));

                setSession(fetchedSession);
                setProblemGroups(processedGroups);
                setProblems(fetchedProblems);
                setSubmissions(userSubmissions);

                const groupedProblems: { [key: number]: Problem[] } = {};

                processedGroups.forEach(group => {
                    groupedProblems[group.id] = [];
                });

                fetchedProblems.forEach(problem => {
                    const groupId = Number(problem.groupId);
                    if (!groupedProblems[groupId]) {
                        groupedProblems[groupId] = [];
                    }
                    groupedProblems[groupId].push({
                        ...problem,
                        groupId: Number(problem.groupId),
                        sessionId: Number(problem.sessionId)
                    });
                });

                setGroups(groupedProblems);

                // Auto-expand first group or partially completed group
                const groupIds = Object.keys(groupedProblems).map(Number);
                if (groupIds.length === 1) {
                    setExpandedGroups(new Set([groupIds[0]]));
                } else {
                    for (const groupId of groupIds) {
                        const groupProblems = groupedProblems[groupId];
                        const solved = groupProblems.filter(p =>
                            userSubmissions.some(sub => sub.problemId === p.id && sub.isCorrect)
                        ).length;
                        if (solved > 0 && solved < groupProblems.length) {
                            setExpandedGroups(new Set([groupId]));
                            break;
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching problems:', err);
                setError('Failed to load problems. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sessionId]);

    const isProblemSolved = (problemId: number): boolean => {
        return submissions.some(sub => sub.problemId === problemId && sub.isCorrect);
    };

    const getSubmissionStatus = (problemId: number): { label: string; color: 'default' | 'success' | 'error'; icon?: React.ReactElement } => {
        const submission = submissions.find(sub => sub.problemId === problemId);

        if (!submission) {
            return { label: 'Not Started', color: 'default' };
        }

        if (submission.isCorrect) {
            return { label: 'Correct', color: 'success', icon: <CheckIcon /> };
        }

        return { label: 'Incorrect', color: 'error', icon: <CloseIcon /> };
    };

    const isGroupEnded = (groupId: number): boolean => {
        const group = problemGroups.find(g => Number(g.id) === groupId);

        if (!group || !group.endDate) {
            return false;
        }

        const now = new Date();
        const endDate = new Date(group.endDate);
        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        return nowDateOnly > endDateOnly;
    };

    const getGroupTitle = (groupId: number): string => {
        const group = problemGroups.find(g => Number(g.id) === groupId);
        return group ? group.title : `Problem Group ${groupId}`;
    };

    const toggleGroup = (groupId: number) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const totalProblems = problems.length;
    const solvedProblems = submissions.filter(s => s.isCorrect).length;
    const accuracy = submissions.length > 0
        ? Math.round((submissions.filter(s => s.isCorrect).length / submissions.length) * 100)
        : 0;
    const completionRate = totalProblems > 0 ? Math.round((solvedProblems / totalProblems) * 100) : 0;

    if (loading) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </StudentLayout>
        );
    }

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            {session?.title || 'Session Problems'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {session?.description || 'Work on math problems for this session and track your progress.'}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/student/v2')}
                    >
                        Back to Dashboard
                    </Button>
                </Box>

                {error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                ) : problems.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        No problems found for this session yet. Check back later!
                    </Alert>
                ) : (
                    <>
                        {/* Stats Overview */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main, mr: 2 }}>
                                                <AssignmentIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {totalProblems}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Problems
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)` }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.main, mr: 2 }}>
                                                <CheckIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {solvedProblems}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Solved
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)` }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.main, mr: 2 }}>
                                                <TrophyIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {accuracy}%
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Accuracy
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)` }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.main, mr: 2 }}>
                                                <AssignmentIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {completionRate}%
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Completion
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Problem Groups */}
                        <Box>
                            {Object.keys(groups).map((groupIdStr) => {
                                const groupId = parseInt(groupIdStr);
                                const groupProblems = groups[groupId];
                                const solvedInGroup = groupProblems.filter(p => isProblemSolved(p.id)).length;
                                const totalInGroup = groupProblems.length;
                                const completionPercentage = Math.round((solvedInGroup / totalInGroup) * 100);
                                const isExpanded = expandedGroups.has(groupId);
                                const group = problemGroups.find(g => g.id === groupId);
                                const hasVideo = group?.hintsVideoUrl;

                                return (
                                    <Card
                                        key={groupId}
                                        elevation={0}
                                        sx={{
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.divider}`,
                                            mb: 3,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {/* Group Header */}
                                        <Box
                                            sx={{
                                                p: 3,
                                                bgcolor: completionPercentage === 100
                                                    ? alpha(theme.palette.success.main, 0.08)
                                                    : completionPercentage > 0
                                                        ? alpha(theme.palette.primary.main, 0.08)
                                                        : 'background.paper',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: completionPercentage === 100
                                                        ? alpha(theme.palette.success.main, 0.12)
                                                        : completionPercentage > 0
                                                            ? alpha(theme.palette.primary.main, 0.12)
                                                            : alpha(theme.palette.action.hover, 0.05)
                                                }
                                            }}
                                            onClick={() => toggleGroup(groupId)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: completionPercentage === 100
                                                                ? alpha(theme.palette.success.main, 0.15)
                                                                : alpha(theme.palette.primary.main, 0.15),
                                                            color: completionPercentage === 100
                                                                ? theme.palette.success.main
                                                                : theme.palette.primary.main
                                                        }}
                                                    >
                                                        {completionPercentage === 100 ? <CheckIcon /> : <AssignmentIcon />}
                                                    </Avatar>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {getGroupTitle(groupId)}
                                                    </Typography>
                                                    {isGroupEnded(groupId) && (
                                                        <Chip
                                                            label="Ended"
                                                            icon={<ScheduleIcon />}
                                                            size="small"
                                                            color="default"
                                                        />
                                                    )}
                                                    {hasVideo && (
                                                        <Chip
                                                            label="Hint Video"
                                                            icon={<VideoIcon />}
                                                            size="small"
                                                            color="secondary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/hint-videos/${groupId}`);
                                                            }}
                                                            clickable
                                                        />
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {solvedInGroup} / {totalInGroup}
                                                    </Typography>
                                                    <Chip
                                                        label={completionPercentage === 100 ? 'Completed' : `${completionPercentage}%`}
                                                        color={completionPercentage === 100 ? 'success' : 'primary'}
                                                        size="small"
                                                    />
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </Box>
                                            </Box>

                                            <LinearProgress
                                                variant="determinate"
                                                value={completionPercentage}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: completionPercentage === 100
                                                            ? theme.palette.success.main
                                                            : theme.palette.primary.main
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* Group Problems */}
                                        {isExpanded && (
                                            <>
                                                <Divider />
                                                <List sx={{ py: 0 }}>
                                                    {groupProblems.map((problem, index) => {
                                                        const status = getSubmissionStatus(problem.id);
                                                        return (
                                                            <React.Fragment key={problem.id}>
                                                                {index > 0 && <Divider component="li" />}
                                                                <ListItem
                                                                    disablePadding
                                                                    secondaryAction={
                                                                        <Chip
                                                                            label={status.label}
                                                                            color={status.color}
                                                                            size="small"
                                                                            icon={status.icon}
                                                                        />
                                                                    }
                                                                >
                                                                    <ListItemButton
                                                                        onClick={() => navigate(`/v2/sessions/${sessionId}/problems/${problem.id}`)}
                                                                        sx={{
                                                                            py: 2,
                                                                            pr: 14,
                                                                            '&:hover': {
                                                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ListItemAvatar>
                                                                            <Avatar
                                                                                sx={{
                                                                                    bgcolor: isProblemSolved(problem.id)
                                                                                        ? alpha(theme.palette.success.main, 0.15)
                                                                                        : alpha(theme.palette.grey[500], 0.15),
                                                                                    color: isProblemSolved(problem.id)
                                                                                        ? theme.palette.success.main
                                                                                        : theme.palette.grey[600]
                                                                                }}
                                                                            >
                                                                                {isProblemSolved(problem.id) ? (
                                                                                    <CheckIcon />
                                                                                ) : (
                                                                                    <Typography variant="body2" fontWeight="bold">
                                                                                        {index + 1}
                                                                                    </Typography>
                                                                                )}
                                                                            </Avatar>
                                                                        </ListItemAvatar>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Typography variant="subtitle1" fontWeight={500}>
                                                                                    Problem {index + 1}
                                                                                </Typography>
                                                                            }
                                                                            secondary={createTextPreview(problem.problemStatement, 80)}
                                                                        />
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </List>
                                            </>
                                        )}
                                    </Card>
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        </StudentLayout>
    );
};

export default ProblemsPageV2;
